import React from 'react';
import { FieldCompatibleProps } from '../Field';
import { HelpText } from '../HelpText';
import { Intent, useFullTheme } from '~/design';
import { useId } from '~/utils';
import { IconWrapper, InternalTextInput, Label } from '~/internal';
import { EmailOutlineIcon, SearchOutlineIcon } from '@applyboard/ui-icons';

export interface TextInputProps extends FieldCompatibleProps<string, HTMLInputElement> {
  /** The aria-label of the text field. */
  'aria-label'?: string;

  /** The aria-decsribed-by */
  'aria-describedby'?: string;

  /** If true, the text field cannot be interacted with. */
  disabled?: boolean;

  /** The intent of the text field. Maps to color. */
  intent?: Intent;

  /** A callback function that is invoked when a key is pressed. */
  onKeyDown?: (key: React.KeyboardEvent<HTMLInputElement>['key']) => void;

  /** The placeholder of the text field. Placeholders are considered bad for accessibility, so it's *strongly encouraged* to avoid using this.
   * See this article for more details: https://www.smashingmagazine.com/2018/06/placeholder-attribute/ */
  placeholder?: string;

  /** The size of the text field. */
  size?: 'sm' | 'md' | 'lg';

  /** If type = 'search', a search icon will appear on the left side of the text field. */
  type?: 'text' | 'password' | 'search' | 'time' | 'email';
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    'aria-label': ariaLabel,
    disabled = false,
    helpText,
    intent = 'secondary',
    invalid,
    label = '',
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    placeholder,
    required,
    size = 'md',
    type = 'text',
    value = '',
  },
  ref,
) {
  if (!intent) {
    throw new Error('"intent" prop must be supplied.');
  }
  if (!ariaLabel && !label) {
    throw new Error('Either "aria-label" prop or "label" prop must be supplied.');
  }

  const theme = useFullTheme();
  const id = useId();
  const adjustedIntent = invalid ? 'negative' : intent;
  const helpTextId = `${id}-helptext`;

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[2],
      }}
      data-__do_not_use__-cds-element="form-control-root"
    >
      <Label disabled={disabled} htmlFor={id} label={label} required={required} />

      <InternalTextInput
        aria-describedby={helpText ? helpTextId : undefined}
        aria-label={label ? undefined : ariaLabel}
        disabled={disabled}
        id={id}
        intent={adjustedIntent}
        invalid={invalid}
        leadAdornment={
          type === 'search' ? (
            <IconWrapper intent={adjustedIntent} selector="div">
              <SearchOutlineIcon />
            </IconWrapper>
          ) : type === 'email' ? (
            <IconWrapper intent={adjustedIntent} selector="div">
              <EmailOutlineIcon />
            </IconWrapper>
          ) : undefined
        }
        onBlur={() => onBlur?.()}
        onChange={e => onChange(e.target.value)}
        onFocus={() => onFocus?.()}
        onKeyDown={e => onKeyDown?.(e.key)}
        placeholder={placeholder}
        ref={ref}
        required={required}
        size={size}
        type={type}
        value={value}
      />

      {helpText && (
        <HelpText
          id={helpTextId}
          intent={adjustedIntent}
          showIcon={invalid || disabled || intent !== 'secondary'}
        >
          {helpText}
        </HelpText>
      )}
    </div>
  );
});
